<template>
    <FormElement :input-data="inputData">
        <slot name="before-input" />
        <vue-tel-input
            :key="forceReactivity.key.value"
            v-model="phone"
            class="vue-tel-input"
            :class="[inputData.className.value, props.variant === 'inbound' ? 'vti-inbound' : '']"
            mode="international"
            :default-country="locale.toUpperCase()"
            :preferred-countries="preferredCountries"
            :input-options="{
                placeholder: inputData.placeholder.value,
                autocomplete: 'tel',
            }"
            @on-input="onPhoneInput"
            @focus="onPhoneFocus"
        />
        <slot name="after-input" />
    </FormElement>
</template>
<script setup lang="ts">
    import { Str } from '@supercharge/strings';
    import { Locale } from '~/app/enums/Locale';
    import { FormInputProps, FormInputData } from '~~/composables/useFormInput';

    const { tenant } = useAuthStore();
    const props = defineProps<FormInputProps & { preferredLocale?: Locale }>();
    const inputData: FormInputData = useFormInput(props);
    const input = ref<HTMLInputElement | null>(null);
    const phone = ref<string>(inputData.model.value);
    const forceReactivity = useForceReactivity();
    const hasFocused = ref(false);
    const onPhoneFocus = () => {
        hasFocused.value = true;
    };

    // Store the parsed E.164 number (not the raw display) so autofill can't submit national format. See TDD-3401.
    const onPhoneInput = (value: string, phoneObject?: { valid?: boolean; number?: string }) => {
        if (!hasFocused.value) {
            return;
        }

        inputData.model.value =
            phoneObject?.valid && phoneObject.number
                ? phoneObject.number
                : Str(value ?? '')
                      .strip()
                      .get();
    };

    const preferredCountries = computed(() => {
        if (tenant?.billingCountry === 'fr' || props.preferredLocale?.startsWith('fr')) {
            return ['fr', 'be', 'nl', 'de', 'lu'];
        }

        return ['be', 'fr', 'nl', 'de', 'lu'];
    });

    const locale = computed(() => {
        return props.form.get('locale', props.preferredLocale).substring(0, 2);
    });

    watch(locale, () => {
        forceReactivity.force();
    });

    onMounted(async () => {
        if (props.autofocus && input.value) {
            await wait(250);
            input.value?.focus();
        }
    });
</script>

<style lang="scss">
    // Override the vue-tel-input library's wrapper styles with DS values.
    // The library sets: border: 1px solid #bbb; border-radius: 3px; background-color: transparent
    // On :focus-within: box-shadow: ...; border-color: #66afe9
    // We override with DS values using !important to guarantee we beat the library regardless
    // of CSS load order. The DS error/validated/focus states also use !important (e.g.
    // focus:!border-black, !border-[#ff4e4e]) so they still win in the cascade.
    .vue-tel-input {
        border: 1px solid #d4d4d8 !important;
        // border-radius not set — let Tailwind size class handle it
        box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.08) !important;
        background-color: white !important;
        display: flex;
        align-items: center;

        &:focus-within {
            border-color: black !important; // matches focus:!border-black
            box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.08) !important;
        }

        .auth-form & {
            gap: 8px;
            padding: 0;
        }
    }

    // Inner input: strip all styling so it inherits from the DS wrapper
    .vti__input {
        border: none !important;
        outline: none !important;
        background: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0;
        height: auto;
        font: inherit;
        color: inherit;
        width: 100%;

        &::placeholder {
            color: #a1a1aa;
            font-weight: 400;
        }
    }

    .vti__dropdown {
        padding: 0;
        border: none;
        background: transparent;
        display: flex;
        align-items: center;
        margin-right: 8px;

        .auth-form & {
            border: 1px solid #d9d9d9;
            border-radius: 8px;
            padding-inline: 10px;
        }
    }

    .vti__dropdown-arrow {
        display: none;
    }

    .vti__dropdown-list {
        padding: 12px;
        filter: drop-shadow(0 1px 2px rgb(0 0 0 / 0.1)) drop-shadow(0 1px 1px rgb(0 0 0 / 0.06));
        border-radius: 8px;
        border-color: #f3f4f6;
    }

    .vti__dropdown-item {
        padding: 6px 10px;
        border-radius: 8px;
        font-size: 13px;
        display: flex;
        align-items: center;
    }

    .vti__dropdown-item.highlighted {
        background: #f3f4f6;
    }

    .vti__dropdown-item.last-preferred {
        margin-bottom: 20px;
        position: relative;
        border-bottom: none;
    }

    .vti__dropdown-item.last-preferred:after {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 0;
        right: 0;
        height: 1px;
        background: #e5e7eb;
    }

    .vti__dropdown-item .vti__flag {
        margin-right: 12px;
    }

    .vti__dropdown-item strong {
        font-weight: 400;
        margin-right: 5px;
    }

    .vti__dropdown-arrow {
        display: none;
    }

    .vue-tel-input:focus-within {
        box-shadow: none;
        border-color: #0f1020 !important;
    }

    /* .vti__flag {
        width: 16px;
        height: 12px;
    } */

    .vti__dropdown {
        padding: 0;
    }

    /* ─── Inbound variant ───────────────────────────────────────────────────────────────
       Make the phone field match the other inbound inputs (FormInput / FormInboundSelect).
       The wrapper box (height / radius / border / shadow / bg) already comes from
       form.inbound.input via :class; here we flatten the library internals into it and add the
       inbound focus ring. The default/app style above is untouched. */
    .vue-tel-input.vti-inbound {
        padding-block: 0;
        gap: 0;

        &:focus-within {
            border-color: var(--tp-fg-2);
            box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
        }

        // Country selector (flag) segment: flat, no boxed border, sits inside the wrapper padding.
        .vti__dropdown {
            background: transparent;
            border: 0;
            border-radius: var(--tp-radius-md);
            padding: 0 4px 0 0;

            &:hover,
            &.open {
                background: var(--tp-bg-2);
            }
        }

        // Actual phone text field: transparent, borderless, inbound typography.
        .vti__input {
            background: transparent;
            color: var(--tp-fg-2);
            font-family: inherit;
            font-size: 16px;
            border: 0 !important;
            outline: none !important;
            box-shadow: none !important;
            padding: 0 0 0 8px;

            &::placeholder {
                color: var(--tp-fg-6);
            }
        }

        // Country popup: match the FormInboundSelect popup.
        .vti__dropdown-list {
            border: 1px solid var(--tp-border-1);
            border-radius: var(--tp-radius-md);
            box-shadow: var(--tp-shadow-popup);
            padding: 8px;
            filter: none;
        }
        .vti__dropdown-item {
            border-radius: 8px;
            padding: 8px 12px;
            color: var(--tp-fg-2);
            font-size: 14px;

            &.highlighted {
                background: var(--tp-bg-2);
            }
        }
    }
</style>
